import React, { type JSX } from 'react';
import {
    Box,
    FormControl,
    FormControlLabel,
    FormHelperText,
    FormLabel,
    InputLabel,
    MenuItem,
    Radio,
    RadioGroup,
    Stack,
    Typography,
} from '@mui/material';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTypedSelector } from '@fiji/common';
import { setMessageContent } from '@fiji/common/src/features/common/commonSlice';
import {
    useCreateLoadGroupsMutation,
    useGetMultiDeviceQuery,
} from '@fiji/common/src/features/loadGroups/loadGroupsApi';
import { useGetDeviceByIdQuery } from '@fiji/common/src/features/deviceManagement/deviceApi';
import { CustomModal, type ModalActions } from 'components/CustomModal';
import { CustomTransComponent, GroupPath, StepperModal } from 'components';
import LanguageTextField from 'components/FormElements/LanguageTextField';
import { useNewCustomTranslations, useTextFieldError } from 'hooks';
import { LoadHierarchy } from './LoadHierarchy';
import { LoadStepTwo } from './LoadStepTwo';
import { MultiDeviceLoadStep } from './MultiDeviceLoadStep';

type Props = {
    isOpen: boolean;
    onClose: () => void;
    deviceId?: string;
    groupId?: string;
};

export const CreateLoadGroupModal = ({ isOpen, onClose, deviceId, groupId }: Props): JSX.Element => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const currentRealmName = useTypedSelector((state) => state.common.selectedRealm);
    useNewCustomTranslations('WIDGETS');
    const { hasTextError, makeErrorHandler, resetErrors } = useTextFieldError();

    const [nameError, setNameError] = React.useState('');
    const [isInputModalOpen, setIsInputModalOpen] = React.useState(false);
    const [isStepperModalOpen, setIsStepperModalOpen] = React.useState(false);
    const [currentStep, setCurrentStep] = React.useState<string>('selectDevice');
    const [selectedDeviceType, setSelectedDeviceType] = React.useState<string>('');
    const [selectedDeviceId, setSelectedDeviceId] = React.useState<any>({});
    const [selectedMultiLoads, setSelectedMultiLoads] = React.useState<any[]>([]);

    const [createPayload, setCreatePayload] = React.useState<any>({
        description: '',
        modelCategory: '',
        isMultipleDevice: false,
        loadIds: [],
        name: '',
        ...(deviceId && { sourceDeviceId: deviceId }),
        ...(groupId && { sourceGroupId: groupId }),
    });

    const resetCreatePayload = (): void => {
        setCreatePayload({
            description: '',
            modelCategory: '',
            isMultipleDevice: false,
            loadIds: [],
            name: '',
            ...(deviceId && { sourceDeviceId: deviceId }),
            ...(groupId && { sourceGroupId: groupId }),
        });
        setSelectedDeviceId({});
        setSelectedDeviceType('');
        setSelectedMultiLoads([]);
        setNameError('');
        setCurrentStep('selectDevice');
        setIsStepperModalOpen(false);
        resetErrors();
    };

    const handleClose = (): void => {
        setIsInputModalOpen(false);
        resetCreatePayload();
        onClose();
    };

    React.useEffect(() => {
        setIsInputModalOpen(isOpen);
        if (!isOpen) {
            setIsStepperModalOpen(false);
            resetErrors();
        }
    }, [isOpen, resetErrors]);

    React.useEffect(() => {
        resetErrors();
    }, [currentStep, resetErrors]);

    const [createLoadGroup, { isLoading: isCreateLoadGroupLoading }] = useCreateLoadGroupsMutation();
    const { data: multiDeviceData } = useGetMultiDeviceQuery({
        params: { category: 'CATEGORY', assetCategory: 'LOAD_GROUPS' },
    });

    const { data: deviceData }: any = useGetDeviceByIdQuery(createPayload?.sourceDeviceId ?? deviceId, {
        skip: !deviceId && !createPayload?.sourceDeviceId,
    });

    React.useEffect(() => {
        if (deviceData?.data?.modelCategory) {
            setCreatePayload((prev: any) => ({
                ...prev,
                modelCategory: deviceData.data.modelCategory,
            }));
        }
    }, [deviceData]);

    const handleMultiDeviceChange = (event: SelectChangeEvent): void => {
        const value = event.target.value;
        setSelectedDeviceType(value);
        setCreatePayload((prev: any) => ({
            ...prev,
            modelCategory: value,
        }));
    };

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
        const isMultipleDevice = event?.target?.value === 'true';
        setCreatePayload((pre: any) => ({
            ...pre,
            isMultipleDevice,
            loadIds: [],
        }));
        setSelectedMultiLoads([]);
    };

    const handleChangeNameDescription = (e: any): void => {
        const { name, value } = e.target;
        if (name === 'name') {
            if (value.startsWith(' ')) {
                setNameError('Please provide a valid name.');
            } else {
                setNameError('');
            }
        }

        setCreatePayload((prev: any) => ({
            ...prev,
            [name]: value,
        }));
    };

    const modalContent = React.useMemo(
        () =>
            createPayload?.isMultipleDevice
                ? [
                      {
                          key: 'selectLoads',
                          title: t('WIDGETS:LOADS_GROUPS.CREATE_LOAD_GROUP'),
                          header: t('WIDGETS:LOADS_GROUPS.SELECT_LOADS_TO_ADD_TO_THE_LOAD_GROUP'),
                          description: selectedDeviceType,
                          component: (
                              <MultiDeviceLoadStep
                                  createPayload={createPayload}
                                  setCreatePayload={setCreatePayload}
                                  selectedLoads={selectedMultiLoads}
                                  setSelectedLoads={setSelectedMultiLoads}
                              />
                          ),
                      },
                  ]
                : [
                      {
                          key: 'selectDevice',
                          title: t('WIDGETS:LOADS_GROUPS.CREATE_LOAD_GROUP'),
                          description: '',
                          header: t('WIDGETS:LOADS_GROUPS.SELECT_DEVICE_TO_CREATE_A_LOAD_GROUP'),
                          component: (
                              <LoadHierarchy
                                  setSelectDeviceId={(device: any) => {
                                      setSelectedDeviceId(device);
                                      setCreatePayload((prev: any) => ({
                                          ...prev,
                                          sourceDeviceId: device?.id,
                                      }));
                                  }}
                                  selectedDeviceId={selectedDeviceId?.id}
                              />
                          ),
                      },
                      {
                          key: 'selectLoads',
                          title: t('WIDGETS:LOADS_GROUPS.CREATE_LOAD_GROUP'),
                          header: t('WIDGETS:LOADS_GROUPS.SELECT_LOADS_TO_ADD_TO_THE_LOAD_GROUP'),
                          description: (
                              <GroupPath deviceName={selectedDeviceId?.name} pathName={selectedDeviceId?.groupPath} />
                          ),
                          component: (
                              <LoadStepTwo
                                  selectedDevice={selectedDeviceId}
                                  createPayload={createPayload}
                                  setCreatePayload={setCreatePayload}
                              />
                          ),
                      },
                  ],
        [selectedDeviceId?.id, selectedDeviceType, selectedMultiLoads, createPayload, t]
    );

    const handleCreateLoadGroup = async (): Promise<any> => {
        const payloadToSend = {
            ...createPayload,
            modelCategory: createPayload.modelCategory,
        };

        const { error, data: loadGroup }: any = await createLoadGroup(payloadToSend);

        if (!error) {
            setIsStepperModalOpen(false);
            handleClose();
            navigate(`/${currentRealmName}/load-groups/${loadGroup?.data?.loadGroup?.id}`, {
                state: {
                    from: location.pathname + location.hash,
                },
            });
            dispatch(
                setMessageContent({
                    isOpen: true,
                    message: t('WIDGETS:LOADS_GROUPS.LOAD_GROUP_CREATED_SUCCESSFULLY'),
                    duration: 3000,
                })
            );
        }
    };

    const handleCheckDisabled = (): boolean => {
        switch (currentStep) {
            case 'selectDevice':
                return !selectedDeviceId?.id?.length;
            case 'selectLoads':
                return !createPayload?.loadIds?.length;
            default:
                return true;
        }
    };

    const deleteModalActions: ModalActions[] = [
        {
            key: 'Cancel',
            label: <CustomTransComponent translationKey={'COMMON:CANCEL'} />,
            close: true,
            variant: 'outlined',
            handleClick: (): void => {
                if (currentStep === 'stepOne') {
                    setIsStepperModalOpen(false);
                }
            },
        },
        {
            key: 'Next',
            disabled:
                !createPayload?.name?.length ||
                (createPayload?.isMultipleDevice && !selectedDeviceType) ||
                isCreateLoadGroupLoading ||
                hasTextError,
            label: <CustomTransComponent translationKey={'COMMON:NEXT'} />,
            variant: 'contained',
            handleClick: (): void => {
                if (createPayload?.isMultipleDevice) {
                    navigate(`/${currentRealmName}/load-groups`, {
                        state: {
                            from: location.pathname + location.hash,
                            isDraft: true,
                            createPayload: {
                                ...createPayload,
                                loadIds: selectedMultiLoads?.map?.((load: any) => load?.id || load?.loadId) ?? [],
                            },
                        },
                    });
                    handleClose();
                    return;
                }

                setIsInputModalOpen(false);
                setCurrentStep('selectDevice');
                setIsStepperModalOpen(true);
            },
        },
    ];

    return (
        <>
            <CustomModal
                isOpen={isInputModalOpen}
                isLoading={false}
                type="primary"
                onClose={handleClose}
                actions={deleteModalActions}
                customHeader={
                    <>
                        <CustomTransComponent translationKey={'WIDGETS:LOADS_GROUPS.CREATE_LOAD_GROUP'} />
                    </>
                }
                actionsDivider={true}
            >
                <Box>
                    <Typography variant="subtitle1" fontWeight={'600'} my={2} sx={{ wordWrap: 'break-word' }}>
                        <CustomTransComponent
                            translationKey={'WIDGETS:LOADS_GROUPS.NEW_LOAD_GROUP_NAME_AND_DESCRIPTION'}
                        />
                    </Typography>
                    <Box>
                        <LanguageTextField
                            id="name"
                            label={<CustomTransComponent translationKey={'COMMON:NAME'} />}
                            name="name"
                            multiline
                            variant="filled"
                            value={createPayload?.name}
                            onChange={handleChangeNameDescription}
                            fullWidth
                            required
                            type="text"
                            inputProps={{ maxLength: 32 }}
                            {...(nameError && {
                                error: true,
                                helperText: nameError,
                            })}
                            handleTextError={makeErrorHandler('name')}
                            className="margin-top-18"
                        />
                        <Stack direction={'row'} justifyContent={'space-between'} px={2}>
                            <FormHelperText>
                                <CustomTransComponent translationKey={'COMMON:REQUIRED_LABEL'} />
                            </FormHelperText>
                            <FormHelperText>{createPayload?.name?.length}/32</FormHelperText>
                        </Stack>
                        <LanguageTextField
                            id="description"
                            label={<CustomTransComponent translationKey={'COMMON:DESCRIPTION'} />}
                            name="description"
                            multiline
                            variant="filled"
                            value={createPayload?.description}
                            onChange={handleChangeNameDescription}
                            fullWidth
                            inputProps={{ maxLength: 100 }}
                            handleTextError={makeErrorHandler('description')}
                            className="margin-top-18"
                        />
                        <FormHelperText className="text-align-end padding-x-16">
                            {createPayload?.description?.length}/100
                        </FormHelperText>
                    </Box>
                    <Box>
                        <FormControl>
                            <FormLabel id="load-group-type-selection" sx={{ py: 3 }}>
                                <CustomTransComponent
                                    translationKey={'WIDGETS:LOADS_GROUPS.NEW_LOAD_GROUP_SUB_HEADING'}
                                />
                            </FormLabel>
                            <Box>
                                <RadioGroup
                                    aria-labelledby="load-group-type-selection"
                                    name="load-group-type-selection"
                                    value={String(createPayload?.isMultipleDevice)}
                                    onChange={handleChange}
                                >
                                    <FormControlLabel
                                        value={false}
                                        control={<Radio />}
                                        label={t('WIDGETS:LOADS_GROUPS.SPECIFIC_TO_A_SINGLE_DEVICE')}
                                    />
                                    <FormControlLabel
                                        value={true}
                                        control={<Radio />}
                                        label={t('WIDGETS:LOADS_GROUPS.CONTAINS_LOADS_ON_MULTIPLE_DEVICES')}
                                    />
                                </RadioGroup>
                            </Box>
                        </FormControl>
                        {createPayload?.isMultipleDevice && (
                            <Box mt={2} mb={3}>
                                <FormControl variant="filled" fullWidth>
                                    <InputLabel id="multi-device-type-select">
                                        <CustomTransComponent
                                            translationKey={'WIDGETS:LOADS_GROUPS.MULTI_DEVICE_TYPE_SELECTION'}
                                        />
                                    </InputLabel>
                                    <Select
                                        labelId="multi-device-type-select"
                                        value={selectedDeviceType}
                                        onChange={handleMultiDeviceChange}
                                    >
                                        {Array.isArray(multiDeviceData?.data) &&
                                            multiDeviceData.data.map((item: string) => (
                                                <MenuItem key={item} value={item}>
                                                    {item}
                                                </MenuItem>
                                            ))}
                                    </Select>
                                </FormControl>
                            </Box>
                        )}
                    </Box>
                </Box>
            </CustomModal>

            <StepperModal
                maxWidth={'lg'}
                modalContent={modalContent}
                isOpen={isStepperModalOpen}
                closeModal={handleClose}
                getCurrentStep={(step: string): void => setCurrentStep(step)}
                currentStepPassed={currentStep}
                setCurrentStep={setCurrentStep}
                isDisabled={handleCheckDisabled()}
                gotoBackStep={(): any => setCurrentStep('selectDevice')}
                submitHandler={(): any => handleCreateLoadGroup()}
                sx={{ height: 'calc(100vh - 150px)', width: '900px' }}
                isNextButton={false}
            />
        </>
    );
};
